import React, { ReactElement, memo } from "react";
import { ViewStyle } from "react-native";

import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import Header from "@/features/roster-management/components/molecules/Header";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import SkeletonList from "@/components/organisms/SkeletonList";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { RosterManagementTeamFragment } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Props for the TeamsView component
 */
interface Props {
  teams: RosterManagementTeamFragment[];
  loading?: boolean;
  refreshing: boolean;
  renderItem: ListRenderItem<RosterManagementTeamFragment>;
  onLoadMore: () => void;
  onRefresh: () => void;
  onSearch: (value: string) => void;
  renderListFooterComponent: () => ReactElement | null;
  keyExtractor: (item: RosterManagementTeamFragment) => string;
  contentContainerStyle: ViewStyle;
  refreshControlColors: string[];
}

/**
 * Empty state shown when no teams are available for the organization.
 * Prompts users to associate leagues with their organization to view teams.
 */
const ListEmptyComponent = () => (
  <Box className="h-4/5 items-center justify-center">
    <Text variant="unstyled" className={ifWeb("text-title-sm text-center", "")}>
      There are no eligible teams for your organization. Please associate
      leagues with your organization to view teams.
    </Text>
  </Box>
);

/**
 * View for displaying a searchable, scrollable list of teams.
 * Receives memoized callbacks and styles from Container to prevent unnecessary re-renders.
 * @param root0 - Component props
 * @param root0.teams - Array of team items to display
 * @param root0.loading - Whether initial data is loading
 * @param root0.refreshing - Whether pull-to-refresh is active
 * @param root0.renderItem - Function to render each team list item
 * @param root0.onLoadMore - Callback when scrolling near end to load more teams
 * @param root0.onRefresh - Callback to refresh team data
 * @param root0.onSearch - Callback when search query changes with search value
 * @param root0.renderListFooterComponent - Function to render custom list footer
 * @param root0.keyExtractor - Memoized function to extract unique key for each team item
 * @param root0.contentContainerStyle - Memoized styles for the content container
 * @param root0.refreshControlColors - Memoized array of colors for the refresh control
 */
const TeamsView = ({
  teams,
  loading,
  refreshing,
  renderItem,
  onLoadMore,
  onRefresh,
  onSearch,
  renderListFooterComponent,
  keyExtractor,
  contentContainerStyle,
  refreshControlColors,
}: Props) => (
  <Box className="flex-1 pb-4">
    <FlashList
      contentContainerClassName="self-center flex-grow p-3"
      scrollEventThrottle={16}
      refreshing={refreshing}
      data={teams}
      keyExtractor={keyExtractor}
      contentContainerStyle={contentContainerStyle}
      showsVerticalScrollIndicator={false}
      renderItem={renderItem}
      ListEmptyComponent={
        loading ? <SkeletonList type="manage-roster" /> : ListEmptyComponent
      }
      ItemSeparatorComponent={ItemSeparator}
      ListHeaderComponent={
        <Header onChangeText={onSearch} placeholder="Search Team" />
      }
      ListFooterComponent={renderListFooterComponent}
      onEndReached={onLoadMore}
      onRefresh={onRefresh}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={refreshControlColors}
          tintColor="white"
        />
      }
    />
  </Box>
);

TeamsView.displayName = "TeamsView";

export default memo(TeamsView);
